import { useMemo, useState } from "react";
import { CheckCheck } from "lucide-react";
import { TaskForm, TaskRow } from "./taskWidgets";
import { taskBucket, type TaskBucket } from "./format";
import type { AdminLead, LeadTask } from "./types";
import type { TaskInput } from "./api";

const BUCKETS: { key: TaskBucket; label: string; tone: string }[] = [
  { key: "overdue", label: "Przeterminowane", tone: "red" },
  { key: "today", label: "Dziś", tone: "amber" },
  { key: "tomorrow", label: "Jutro", tone: "blue" },
  { key: "week", label: "W tym tygodniu", tone: "blue" },
  { key: "later", label: "Później", tone: "muted" },
  { key: "done", label: "Zrobione", tone: "green" }
];

export function TasksView({
  tasks,
  leads,
  onCreate,
  onToggle,
  onReschedule,
  onDelete,
  onOpenLead
}: {
  tasks: LeadTask[];
  leads: AdminLead[];
  onCreate: (input: TaskInput) => Promise<void>;
  onToggle: (task: LeadTask, done: boolean) => void;
  onReschedule: (task: LeadTask) => void;
  onDelete: (task: LeadTask) => void;
  onOpenLead: (id: string) => void;
}) {
  const [ownerFilter, setOwnerFilter] = useState<"all" | "__none__" | string>("all");
  const [showDone, setShowDone] = useState(false);

  const leadMap = useMemo(() => new Map(leads.map((lead) => [lead.id, lead])), [leads]);
  const owners = useMemo(() => {
    const set = new Set<string>();
    tasks.forEach((task) => {
      if (task.owner) set.add(task.owner);
    });
    return [...set].sort((a, b) => a.localeCompare(b, "pl"));
  }, [tasks]);

  const filtered = useMemo(
    () =>
      tasks.filter((task) => {
        if (ownerFilter === "__none__" && task.owner) return false;
        if (ownerFilter !== "all" && ownerFilter !== "__none__" && task.owner !== ownerFilter) return false;
        return true;
      }),
    [tasks, ownerFilter]
  );

  const grouped = useMemo(() => {
    const groups: Record<TaskBucket, LeadTask[]> = {
      overdue: [],
      today: [],
      tomorrow: [],
      week: [],
      later: [],
      done: []
    };
    filtered.forEach((task) => groups[taskBucket(task.dueAt, task.done)].push(task));
    (Object.keys(groups) as TaskBucket[]).forEach((key) =>
      groups[key].sort((a, b) => a.dueAt.localeCompare(b.dueAt))
    );
    return groups;
  }, [filtered]);

  const openCount = filtered.filter((task) => !task.done).length;
  const visibleBuckets = BUCKETS.filter((b) => (b.key === "done" ? showDone : true) && grouped[b.key].length);

  return (
    <div className="crm-view">
      <section className="crm-card">
        <TaskForm leads={leads} onSubmit={onCreate} />
      </section>

      <div className="crm-table-meta">
        <span>
          <strong>{openCount}</strong> {openCount === 1 ? "otwarte zadanie" : "otwartych zadań"}
        </span>
        <div className="crm-tasks-filters">
          <select className="crm-select" value={ownerFilter} onChange={(event) => setOwnerFilter(event.target.value)}>
            <option value="all">Wszyscy opiekunowie</option>
            <option value="__none__">Nieprzypisane</option>
            {owners.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
          <label className="crm-check-inline">
            <input type="checkbox" checked={showDone} onChange={(event) => setShowDone(event.target.checked)} />
            Pokaż zrobione
          </label>
        </div>
      </div>

      {openCount === 0 && (
        <div className="crm-state crm-tasks-empty">
          <CheckCheck size={32} aria-hidden="true" />
          <p>Brak otwartych zadań — wszystko ogarnięte 🎉</p>
        </div>
      )}

      {visibleBuckets.map((bucket) => (
        <section className="crm-card crm-task-group" key={bucket.key}>
          <header className="crm-task-group-head">
            <span className={`crm-dot ${bucket.tone}`} />
            <h3>{bucket.label}</h3>
            <span className="crm-task-group-count">{grouped[bucket.key].length}</span>
          </header>
          <div className="crm-task-list">
            {grouped[bucket.key].map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                lead={task.leadId ? leadMap.get(task.leadId) ?? null : null}
                onToggle={onToggle}
                onReschedule={onReschedule}
                onDelete={onDelete}
                onOpenLead={onOpenLead}
              />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
